import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SalesPersonForm, {SalesPersonFormValues} from "../components/forms/SalesPersonForm";
import SalesPersonService from "../services/SalesPersonService";
import {DUPLICATE_LAST_NAME, SalesPerson, DbCustomer} from "sharedlibs";
import {BackendError} from "../utils/helperFunctions";
import PageTitle from "../components/subcomponents/PageTitle";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {Spacer} from './UpdateCustomerPage';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {FormIds} from "../constants";
import {useUserRole} from "../utils/UserRoleContext";
import {logBusinessAudit, salesPersonUpdateAuditMessage} from "../utils/auditLogHelper";
import CustomerService from "../services/CustomerService";
import AssignedCustomersTable from "../components/salesperson/AssignedCustomersTable";
import AssignCustomersDialog from "../components/salesperson/AssignCustomersDialog";

interface UpdateSalesPersonPageProps {
}

export const UpdateSalesPersonPage: React.FC<UpdateSalesPersonPageProps> = ({}) => {

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fallbackURL = searchParams.get('fallbackURL')
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {id} = useParams();
  const {isAdmin} = useUserRole();

  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [formDirty, setFormDirty] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState("")
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success")
  const [loading, setLoading] = React.useState(true)
  // The last name the backend rejected as already taken. Held so the form can mark the field, and
  // cleared as soon as the user edits it — the name they type next may well be free.
  const [duplicateLastName, setDuplicateLastName] = React.useState<string | null>(null)

  const {t} = useTranslation()

  const [salesPerson, setSalesPerson] = React.useState<SalesPerson>()

  const isEditMode = pathname.includes("/salesperson/update") && !!id
  const [assignedCustomers, setAssignedCustomers] = useState<DbCustomer[]>([])
  const [assignedLoading, setAssignedLoading] = useState(false)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  async function loadData() {
    if (pathname.includes("/salesperson/update") && id) {
      setLoading(true)
      let sP
      try {
        sP = await SalesPersonService.getSalesPersonById(id)

        if (sP) {
          setSalesPerson(sP)
        } else {
          console.error('Couldn\t load salesperson from server')
        }
      } catch (e) {
        console.error('Failed loading salesperson, error: ', e)
      }
    }

    setLoading(false)
  }

  useEffect(() => {
    loadData().then()
  }, []);

  useEffect(() => {
    if (!isEditMode || !id) return
    loadAssignedCustomers().then()
  }, [isEditMode, id])

  async function loadAssignedCustomers() {
    if (!id) return
    setAssignedLoading(true)
    try {
      const customers = await CustomerService.getCustomersForSalesperson(id)
      setAssignedCustomers(customers)
    } catch (e) {
      console.error('Failed loading assigned customers:', e)
      showSnackbarMessage(t('salesperson.loadAssignedFailed'), 'error')
    } finally {
      setAssignedLoading(false)
    }
  }

  function showSnackbarMessage(message: string, severity: 'success' | 'error') {
    setShowSnackbar(true)
    setSnackbarSeverity(severity)
    setSnackbarMessage(message)
  }

  async function handleRemoveCustomer(customer: DbCustomer) {
    if (!customer.id) return
    try {
      // The server writes the business audit entry for this change.
      await CustomerService.assignSalesperson(customer.id, null)
      showSnackbarMessage(t('salesperson.removeCustomerSuccess'), 'success')
      await loadAssignedCustomers()
    } catch (e) {
      console.error('Failed to remove customer from salesperson', e)
      showSnackbarMessage(t('salesperson.removeCustomerFailed'), 'error')
    }
  }

  async function handleAssigned(assignedIds: string[]) {
    if (!id) return
    // AssignCustomersDialog saved each assignment through the server, which audits it.
    showSnackbarMessage(t('salesperson.assignCustomersSuccess', {count: assignedIds.length}), 'success')
    await loadAssignedCustomers()
  }

  async function onFormSubmit(salesPersonData: SalesPersonFormValues) {
    const date = new Date().toString()

    if (pathname === '/salesperson/add') {
      const salesPerson = {
        updatedAt: date,
        lastName: salesPersonData.lastName,
        disabled: salesPersonData.disabled,
        createdAt: date
      } as SalesPerson

      try {
        setDuplicateLastName(null)
        await SalesPersonService.addSalesPerson(salesPerson)
        await logBusinessAudit('audit.salesPerson.added', '', null, salesPerson)
        setShowSnackbar(true)
        setSnackbarSeverity("success")
        setSnackbarMessage(t('form.fields.salesperson.snackbar.successfulAdding'))

        if (fallbackURL) {
          navigate(`/${fallbackURL}`)
        }
      } catch (e) {
        console.error(e)
        // Nothing was saved, so stay on the form -- and say WHY when the name is the problem,
        // rather than a generic failure the user cannot act on.
        showSaveError(e, salesPersonData.lastName, 'failedAdding')
      }
    } else {
      if (salesPerson) {
        await updateSalesPerson(salesPerson, salesPersonData)
      } else {
        console.error('No sales person found, couldn\'t edit')

        setShowSnackbar(true)
        setSnackbarSeverity("error")
        setSnackbarMessage(t('form.fields.salesperson.snackbar.failedEdit'))
      }
    }
  }

  async function updateSalesPerson(salesPerson: SalesPerson, salesPersonData: SalesPersonFormValues) {
    const updatedCustomer: SalesPerson = updateSalesPersonObject(salesPerson, salesPersonData)
    try {
      setDuplicateLastName(null)
      await SalesPersonService.editSalesPerson(updatedCustomer)
      await logBusinessAudit(salesPersonUpdateAuditMessage(salesPerson, updatedCustomer), '', salesPerson, updatedCustomer)
      // The page can stay open (no fallbackURL), so the next save is compared with this one.
      setSalesPerson(updatedCustomer)

      setShowSnackbar(true)
      setSnackbarSeverity("success")
      setSnackbarMessage(t('form.fields.salesperson.snackbar.successfulEdit'))

      if (fallbackURL) {
        navigate(`/${fallbackURL}`)
      }
    } catch (e) {
      console.error(e)
      // Previously this always blamed uniqueness, even for a network error. Only say that when
      // the server actually reported a duplicate.
      showSaveError(e, salesPersonData.lastName, 'failedEdit')
    }
  }

  /**
   * Surface a failed save. A 409 from the backend means an active salesperson already holds this
   * last name: name it in the snackbar AND mark the field, so the reason sits next to the input
   * the user has to change. Anything else stays a generic failure.
   */
  function showSaveError(e: unknown, attemptedLastName: string, genericKey: 'failedAdding' | 'failedEdit') {
    const isDuplicate = e instanceof BackendError && e.code === DUPLICATE_LAST_NAME

    if (isDuplicate) {
      setDuplicateLastName(attemptedLastName)
    }

    setShowSnackbar(true)
    setSnackbarSeverity("error")
    setSnackbarMessage(t(isDuplicate
      ? 'form.fields.salesperson.snackbar.failedNotUnique'
      : `form.fields.salesperson.snackbar.${genericKey}`))
  }

  function getDefaultSalesPersonFormValues(): SalesPersonFormValues {
    const defaultFormConfig = {
      lastName: '',
      disabled: false,
    }

    if (pathname === "/salesperson/add") {
      return defaultFormConfig
    }

    // salesPerson is undefined (never null) until a record loads, and stays undefined when the
    // load fails -- guard on truthiness so a failed load falls back to the empty form instead of
    // dereferencing undefined and white-screening the page.
    return salesPerson ? SalesPersonService.mapSalesPersonObjectToFormObject(salesPerson) : defaultFormConfig
  }

  function updateSalesPersonObject(salesPerson: SalesPerson, salesPersonData: SalesPersonFormValues): SalesPerson {
    if (salesPerson.id === undefined) {
      throw new Error("To update a sales person, the id property must exist")
    }

    return {
      id: salesPerson.id,
      createdAt: salesPerson.createdAt,
      disabled: salesPersonData.disabled,
      updatedAt: new Date().toJSON(),
      lastName: salesPersonData.lastName
    }
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.salesperson')}</PageTitle>
      <Divider/>
      <Spacer/>

      <Accordion expanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                          id="update-customer-panel">
          {t('form.salesPerson.salesPersonData')}
        </AccordionSummary>
        <AccordionDetails>
          {
            loading ? <CircularProgress/> :
              <SalesPersonForm onSubmit={onFormSubmit} updateDirty={setFormDirty}
                               defaultValues={getDefaultSalesPersonFormValues()}
                               duplicateLastName={duplicateLastName}
                               onDuplicateResolved={() => setDuplicateLastName(null)}/>
          }
        </AccordionDetails>

        {isAdmin && (
          <AccordionActions>
            <Button
              sx={{marginRight: ".5rem"}}
              type="submit"
              form={FormIds.updateSalesPerson}
              // Blocked while the form still shows a name the backend rejected: re-submitting it
              // unchanged can only fail again.
              disabled={!formDirty || !!duplicateLastName}
            >{t('activities.save')}</Button>
          </AccordionActions>
        )}
      </Accordion>

      {isEditMode && id && (
        <>
          <Spacer/>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}
                              id="assigned-customers-panel">
              <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                <Typography>{t('salesperson.assignedCustomers')}</Typography>
                <Typography color="text.secondary">
                  ({assignedCustomers.length})
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 2}}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon/>}
                  onClick={() => setAssignDialogOpen(true)}
                  disabled={!isAdmin}
                >
                  {t('salesperson.assignCustomers')}
                </Button>
              </Box>
              <AssignedCustomersTable
                customers={assignedCustomers}
                loading={assignedLoading}
                onRemove={handleRemoveCustomer}
                removeDisabled={!isAdmin}
              />
            </AccordionDetails>
          </Accordion>

          <AssignCustomersDialog
            open={assignDialogOpen}
            onClose={() => setAssignDialogOpen(false)}
            onAssigned={handleAssigned}
            salespersonId={id}
          />
        </>
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={6000}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity as "success" | "error"}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
